import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { resetPassword } from '@/api/auth';
import { ApiError } from '@/api/client';

const schema = z
  .object({
    password: z.string().min(12, 'Use at least 12 characters'),
    password_confirmation: z.string(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  // Laravel's reset link is /reset-password/{token}?email=...
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') ?? '';
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setFormError(null);

    try {
      await resetPassword({ token: token ?? '', email, ...values });
      void navigate('/login', { replace: true });
    } catch (error) {
      setFormError(
        error instanceof ApiError
          ? (error.fieldErrors.email?.[0] ?? error.detail ?? error.title)
          : 'Something went wrong. Please try again.',
      );
    }
  };

  if (!token || !email) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', p: 2 }}>
        <Alert severity="error" variant="outlined">
          This reset link is incomplete. Please request a new one.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h1" sx={{ mb: 1 }}>
            Choose a new password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            for {email}
          </Typography>

          {formError && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
            <Stack spacing={2}>
              <TextField
                label="New password"
                type="password"
                autoComplete="new-password"
                autoFocus
                fullWidth
                error={Boolean(errors.password)}
                helperText={errors.password?.message ?? 'At least 12 characters'}
                {...register('password')}
              />

              <TextField
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                fullWidth
                error={Boolean(errors.password_confirmation)}
                helperText={errors.password_confirmation?.message}
                {...register('password_confirmation')}
              />

              <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Reset password'}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
